/**
 * Hook: useMessages
 * Maneja mensajes de un chat con optimistic updates
 */

import { useEffect, useState } from 'react'
import useCRMStore from '../store/useCRMStore'
import { messagesAPI } from '../services/api'

export const useMessages = (phone) => {
  const {
    messages,
    setMessages,
    addMessage,
    updateMessage,
    shouldFetchMessages,
    updateChat,
    sessionExpired
  } = useCRMStore()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [sending, setSending] = useState(false)

  const chatMessages = messages[phone] || []

  // Cargar mensajes cuando cambia el chat seleccionado
  useEffect(() => {
    if (phone && shouldFetchMessages(phone)) {
      loadMessages()
    }
  }, [phone])

  // ========================================
  // AUTO-REFRESH DE MENSAJES
  // ========================================
  useEffect(() => {
    if (!phone || sessionExpired) return

    const interval = setInterval(() => {
      if (sessionExpired) return

      // No refrescar si hay mensajes optimistas pendientes (evita que desaparezcan)
      const currentMsgs = useCRMStore.getState().messages[phone] || []
      const hasOptimistic = currentMsgs.some(m => typeof m.id === 'string' && m.id.startsWith('temp-'))
      if (hasOptimistic) return

      messagesAPI.getMessages(phone).then(data => {
        setMessages(phone, data)
      }).catch(err => {
        console.error('Error en auto-refresh de mensajes:', err)
      })
    }, 10000)

    return () => clearInterval(interval)
  }, [phone, setMessages, sessionExpired])

  const loadMessages = async () => {
    if (!phone || sessionExpired) return

    try {
      setLoading(true)
      setError(null)
      const data = await messagesAPI.getMessages(phone)
      setMessages(phone, data)
    } catch (err) {
      setError(err.message)
      console.error('Error loading messages:', err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Espera a que el servidor tenga mas mensajes que antes del envio,
   * luego reemplaza todo con datos reales (elimina el temp-*).
   * Poll cada 4s, max 10 intentos.
   */
  const waitForServerConfirmation = (targetPhone, tempId, previousCount) => {
    let attempts = 0
    const maxAttempts = 10

    const checker = setInterval(async () => {
      attempts++
      try {
        const serverMessages = await messagesAPI.getMessages(targetPhone)

        // Si el servidor tiene mas mensajes que antes, el webhook ya proceso el envio
        if (serverMessages.length > previousCount || attempts >= maxAttempts) {
          clearInterval(checker)
          setMessages(targetPhone, serverMessages)
        }
      } catch {
        if (attempts >= maxAttempts) {
          clearInterval(checker)
          // Fallback: forzar refresh limpio
          const current = useCRMStore.getState().messages[targetPhone] || []
          setMessages(targetPhone, current.filter(m => m.id !== tempId))
        }
      }
    }, 4000)
  }

  const sendMessage = async (text) => {
    if (!phone || !text.trim() || sessionExpired) return

    const tempId = `temp-${Date.now()}`

    // Contar mensajes reales (sin temp-*) antes del envio
    const currentMsgs = useCRMStore.getState().messages[phone] || []
    const realCount = currentMsgs.filter(m => !(typeof m.id === 'string' && m.id.startsWith('temp-'))).length

    try {
      setSending(true)

      // 1. Optimistic update (agregar mensaje inmediatamente)
      const tempMessage = {
        id: tempId,
        text: text.trim(),
        isFromClient: false,
        timestamp: new Date().toISOString(),
        status: 'sending'
      }
      addMessage(phone, tempMessage)

      // 2. Enviar al servidor
      const response = await messagesAPI.sendMessage(phone, text)

      // 3. Marcar como enviado
      updateMessage(phone, tempId, {
        id: tempId,
        ...response.message,
        status: 'sent'
      })

      // 4. Esperar a que el webhook guarde el mensaje, luego reemplazar con datos reales
      waitForServerConfirmation(phone, tempId, realCount)

      // 5. Actualizar ultimo mensaje en el chat
      updateChat(phone, {
        lastMessage: text,
        lastMessageTime: new Date().toISOString(),
        unreadCount: 0
      })

      return { success: true }
    } catch (err) {
      updateMessage(phone, tempId, { status: 'error' })

      console.error('Error sending message:', err)
      return {
        success: false,
        error: err.response?.data?.error || 'Error al enviar mensaje'
      }
    } finally {
      setSending(false)
    }
  }

  return {
    messages: chatMessages,
    loading,
    error,
    sending,
    sendMessage,
    refresh: loadMessages
  }
}
